import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { ErrorService, LayoutService } from '@core/services';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { takeUntil } from 'rxjs/operators';
import { SelectionOption, StiiraError } from '@core/models';
import { ActionItemsTemplateDetailsStoreService } from '@core/services/action-item-template-details-store.service';
import { ActionItemsTemplateDetails } from '@core/models/action-item-templates/action-item-template-details.model';
import { ActionItemsTemplatesService } from '@core/services/action-item-templates.service';
import { ActionItemsTemplateInfoPost, EditActionItemsTemplateInfoDialog, EditActionItemsTemplateInfoForm } from '@core/models/action-item-templates/edit-action-item-template-info-dialog.model';
import { ProceedConfirmationComponent, ProceedConfirmationComponentData } from '../proceed-confirmation/proceed-confirmation.component';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { OwnerOptions } from '@core/enums/owner-options.enum';

@Component({
    selector: 'app-edit-action-item-template-info',
    templateUrl: './edit-action-item-template-info.component.html',
    styleUrl: './edit-action-item-template-info.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditActionItemsTemplateInfoComponent {
  static readonly componentName = 'EditActionItemsTemplateInfoComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();
  
  public form: FormGroup<EditActionItemsTemplateInfoForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean> = new Subject();
  public isMobile$: Observable<boolean>;
  public leaveTypeOption: SelectionOption[];
  public position = position;
  public onDragEnded = onDragEnded;

  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { 
      templateDetails: ActionItemsTemplateDetails;
      options: EditActionItemsTemplateInfoDialog;
      sysText: any; 
      unsavedChangesSysText: any
    },
    private templateInfoDialogRef: MatDialogRef<EditActionItemsTemplateInfoComponent>,
    private service: ActionItemsTemplatesService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private store: ActionItemsTemplateDetailsStoreService,
    private errorService: ErrorService,
    private layoutService: LayoutService
    ) { 
    this.form = this.fb.group({
      templateName: this.fb.control(null, Validators.required),
      description: this.fb.control(null),
      ownerEmployerId: this.fb.control(null),
      allowUse: this.fb.control(null),
      isDefault: this.fb.control(null),
      allowUseByAllEmployers: this.fb.control(null),
      allowEmployerAssignments: this.fb.control(null)
    })
  }

  ngOnInit(): void {
    closeDialogOnEscape(this.templateInfoDialogRef, this.isSubmitting$, () => this.cancel());

    if (!this.data.templateDetails.ownerEmployerId) {
      this.data.options.employers = [ { id: OwnerOptions.Stock, description: "-- " + this.data.sysText.stock + " --" } ];
    }
    this.isMobile$ = this.layoutService.isMobile$();
    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setFormErrors() {
    setTimeout(() => {
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  public save(): void {

    // warn if global default AND turning off default, disallowing use, changing owner, or turning off sharing
    if (this.data.templateDetails.showIsGlobalDefaultHint 
      && (!this.form.controls.isDefault.value
        || !this.form.controls.allowUse.value
        || (this.form.controls.ownerEmployerId.value > 0 && this.form.controls.ownerEmployerId.value != this.formInitValues?.ownerEmployerId)
        || (
          !this.form.controls.allowUseByAllEmployers.value
          && !this.form.controls.allowEmployerAssignments.value
          && (this.data.templateDetails.allowUseByAllEmployers || this.data.templateDetails.allowEmployerAssignments)))) {
      this.openWarningDialog(this.data.sysText.globalDefaultWarnMsg1, this.data.sysText.globalDefaultWarnMsg2);
    }

    // warn if disallowing use of a template that is a default 
    else if ((this.form.controls.isDefault.value || this.data.templateDetails.employers.filter(er => er.isDefault).length > 0) 
      && !this.form.controls.allowUse.value) {
      this.openWarningDialog(this.data.sysText.allowUseWarnMsg1, this.data.sysText.allowUseWarnMsg2);
    } 

    // warn if turning off sharing and template is a default for an employer other than the owner 
    else if (
      (!this.form.controls.allowUseByAllEmployers.value
        && !this.form.controls.allowEmployerAssignments.value
        && (this.data.templateDetails.allowUseByAllEmployers || this.data.templateDetails.allowEmployerAssignments))
      && this.data.templateDetails.employers.filter(er => er.isDefault && er.id != this.data.templateDetails.ownerEmployerId).length > 0) {
      this.openWarningDialog(this.data.sysText.singleWarnMsg1, this.data.sysText.singleWarnMsg2);
    }
    
    // otherwise continue
    else {
      this.postData();
    }
  }

  public cancel(): void {
    if (this.noChanges) {
      this.templateInfoDialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.templateInfoDialogRef.close();
        }
      });
  }

  private postData(): void {
    const dto: ActionItemsTemplateInfoPost = {
      templateId: this.data.templateDetails.id,
      templateName: this.form.controls.templateName.value,
      description: this.form.controls.description.value,
      ownerEmployerId: this.form.controls.ownerEmployerId.value > 0 ? 
        this.form.controls.ownerEmployerId.value :
        null,
      allowUse: this.form.controls.allowUse.value,
      isDefault: this.form.controls.isDefault.value,
      allowUseByAllEmployers: this.form.controls.allowUseByAllEmployers.value,
      allowEmployerAssignments: this.form.controls.allowEmployerAssignments.value
    }

    this.service.postActionItemsTemplateInfo(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res) => {
        this.store.templateDetails = res as ActionItemsTemplateDetails;
        this.templateInfoDialogRef.close(res);
      }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
  }

  private openWarningDialog(message1: string, message2: string): void {
    this.dialog.open<
      ProceedConfirmationComponent,
      ProceedConfirmationComponentData>
      (ProceedConfirmationComponent, {
        id: ProceedConfirmationComponent.componentName,
        panelClass: "mat-dialog-container-mobileWidth",
        width: '450px',
        disableClose: false,
        closeOnNavigation: true,
        data: {
          title: this.data.sysText.warningTitle,
          message1: message1,
          message2: message2,
          cancelCaption: this.data.sysText.cancel,
          proceedCaption: this.data.sysText.proceed
        }
      }).beforeClosed().subscribe((res: boolean) => {
        if (res) {
          this.postData();
        }
      });
  }

  private setupForm(): void {
    this.leaveTypeOption = this.data.options.leaveTypes;

    this.form.patchValue({
      templateName: this.data.templateDetails.name,
      description: this.data.templateDetails.description,
      ownerEmployerId: this.data.templateDetails.ownerEmployerId ?? OwnerOptions.Stock,
      allowUse: this.data.templateDetails.allowUse,
      isDefault: this.data.templateDetails.isDefault,
      allowUseByAllEmployers: this.data.templateDetails.allowUseByAllEmployers,
      allowEmployerAssignments: this.data.templateDetails.allowEmployerAssignments
    });

    if (!this.data.options.canEditNameAndDescription) {
      this.form.controls.templateName.disable();
      this.form.controls.description.disable();
    }

    if (!this.data.options.canEditAllowUse) {
      this.form.controls.allowUse.disable();
    }

    if (!this.data.options.canEditDefault) {
      this.form.controls.isDefault.disable();
    }

    if (!this.data.options.canEditSharingOptions) {
      this.form.controls.allowUseByAllEmployers.disable();
      this.form.controls.allowEmployerAssignments.disable();
    }

    if (!this.data.options.canEditOwner) {
      this.form.controls.ownerEmployerId.disable();
    }

    this.formInitValues = { ...this.form.value };
    this.setFormErrors();

    this.form.controls.allowUseByAllEmployers.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newValue) => {
        if (newValue)
            this.form.controls.allowEmployerAssignments.setValue(false);
        });

    this.form.controls.allowEmployerAssignments.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newValue) => {
        if (newValue)
          this.form.controls.allowUseByAllEmployers.setValue(false);
        });
  }
}
